import React from "react";
import { graphql, useStaticQuery } from "gatsby";
import Navbar from "../components/Navigation/Navbar";
import Footer from "../components/Footer/Footer";
import SeoHelmet, { SEO } from "../components/Seo/SeoHelmet";
import Opiniones, { FAQ_ITEMS } from "../components/Opiniones/Opiniones";

/**
 * /opiniones — reviews & trust page, rebuilt as a page-scoped Gatsby page
 * (2026-09 redesign, same file-based pattern as /guia-seguro-vida and /loteria).
 *
 * IMPORTANT: this route replaces the CMS-driven page of the same CustomUrl
 * (ES PROD docId fiz3c55oeaiosu5xdp6v59pb). The Strapi page must be
 * unpublished/deleted in the environment where this code deploys, or Gatsby
 * ends up with two components racing for the path.
 *
 * Chrome: same dual-env static query as the guide (PROD yjgd3duk…/fhjyiha8…,
 * TEST hoqjekjh…/u5uwbr7y…) — a missing id resolves to null and the page uses
 * whichever exists.
 */

// FAQPage mirrors the visible FAQ (plain-text answers). No component.accordion
// on this page, so emitting FAQPage here does NOT duplicate the accordion
// auto-emit. No Organization/aggregateRating: self-serving review markup is
// ineligible for rich results and the old CMS schema carried stale numbers.
const OPINIONES_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      "@id": "https://life5.es/opiniones/",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Inicio", item: "https://life5.es/" },
        { "@type": "ListItem", position: 2, name: "Opiniones", item: "https://life5.es/opiniones/" },
      ],
    },
    {
      "@type": "FAQPage",
      mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.plain },
      })),
    },
  ],
};

const OPINIONES_SEO: SEO = {
  SeoTitle: "Opiniones de Life5: reseñas de clientes y por qué es fiable",
  SeoDescription:
    "Reseñas reales de clientes de Life5 en Google (4,4/5). Correduría inscrita en la DGSFP (J3945) y pólizas aseguradas por AXA. Descubre por qué es fiable.",
  // SeoHelmet JSON.stringifies Schema, so it must be the object, not a string.
  Schema: OPINIONES_SCHEMA as unknown as string,
  // Empty → SeoHelmet derives the self-canonical from GATSBY_SITE_URL + customUrl.
  CanonicalTag: "",
  // Indexable only on the production site (GATSBY_* vars are inlined in SSR and browser bundles).
  DoNotIndex: (process.env.GATSBY_SITE_URL || "").replace(/\/$/, "") !== "https://life5.es",
  DoNotFollow: false,
  Links: [],
};

const OpinionesPage = () => {
  const data = useStaticQuery(graphql`
    query OpinionesChromeQuery {
      strapi {
        navbarProd: navbar(documentId: "yjgd3dukwkzwca58wvjmtvgg") {
          DropdownLabel
          DropdownLinks {
            Label
            Url
          }
          SecondDropdownLabel
          SecondDropdownLinks {
            Label
            Url
          }
          Links {
            Label
            Url
          }
          Phone
          HideAccount
          HidePhone
          AccountLabel
          AccountIcon {
            url
          }
          AccountUrl
          ButtonLabel
          ButtonUrl
          idButton
        }
        navbarTest: navbar(documentId: "hoqjekjhmxqm7d059m72x6f6") {
          DropdownLabel
          DropdownLinks {
            Label
            Url
          }
          SecondDropdownLabel
          SecondDropdownLinks {
            Label
            Url
          }
          Links {
            Label
            Url
          }
          Phone
          HideAccount
          HidePhone
          AccountLabel
          AccountIcon {
            url
          }
          AccountUrl
          ButtonLabel
          ButtonUrl
          idButton
        }
        footerProd: footer(documentId: "fhjyiha8yo814fht11mnf11i") {
          group_title_1
          group_title_2
          group_title_3
          group_title_4
          group_links_1 {
            Label
            Url
          }
          group_links_2 {
            Label
            Url
          }
          group_links_3 {
            Label
            Url
          }
          group_links_4 {
            Label
            Url
          }
          group_links_bottom {
            Label
            Url
          }
          contact_address
          contact_phone_number
          contact_phone_subtitle
          contact_title
          contact_map_link {
            Label
            Url
            id
          }
          form_header
          form_submit_button_link {
            Label
            Url
          }
          form_text_placeholder
          facebook_url
          instagram_url
          linkedin_url
          twitter_url
        }
        footerTest: footer(documentId: "u5uwbr7ytsj25k6qa6p0ida3") {
          group_title_1
          group_title_2
          group_title_3
          group_title_4
          group_links_1 {
            Label
            Url
          }
          group_links_2 {
            Label
            Url
          }
          group_links_3 {
            Label
            Url
          }
          group_links_4 {
            Label
            Url
          }
          group_links_bottom {
            Label
            Url
          }
          contact_address
          contact_phone_number
          contact_phone_subtitle
          contact_title
          contact_map_link {
            Label
            Url
            id
          }
          form_header
          form_submit_button_link {
            Label
            Url
          }
          form_text_placeholder
          facebook_url
          instagram_url
          linkedin_url
          twitter_url
        }
      }
    }
  `);

  const navbar = data?.strapi?.navbarProd ?? data?.strapi?.navbarTest;
  const footer = data?.strapi?.footerProd ?? data?.strapi?.footerTest;

  return (
    <>
      <SeoHelmet
        data={OPINIONES_SEO}
        locale="es"
        customUrl="/opiniones/"
        title="Opiniones"
      />
      {navbar && <Navbar data={navbar} locale="es" />}
      <main className="page-content">
        <Opiniones />
      </main>
      {footer && <Footer data={footer} />}
    </>
  );
};

export default OpinionesPage;
